import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Home</Link>
        
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: March 1, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            KwikLuv ("we", "our", or "us") respects your privacy and is committed to protecting your 
            personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard 
            your information when you use our Service.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Email address, password, phone number</li>
            <li><strong>Profile Information:</strong> Name, age, gender, location, bio, interests, photos</li>
            <li><strong>Payment Information:</strong> Processed securely through PayPal (we don't store card details)</li>
            <li><strong>Communications:</strong> Messages you send through the Service</li>
            <li><strong>Verification Photos:</strong> Photos submitted for verification badge (private, admin-only)</li>
          </ul>

          <h3>Information Collected Automatically</h3>
          <ul>
            <li><strong>Usage Data:</strong> Pages visited, features used, time spent</li>
            <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
            <li><strong>Location Data:</strong> Approximate location based on IP or GPS (if permitted)</li>
            <li><strong>Cookies:</strong> Session cookies for authentication and preferences</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Create and manage your account</li>
            <li>Process payments and transactions</li>
            <li>Match you with other users based on preferences</li>
            <li>Send you notifications and updates</li>
            <li>Respond to your inquiries and support requests</li>
            <li>Detect and prevent fraud, abuse, and security issues</li>
            <li>Comply with legal obligations</li>
            <li>Improve and personalize the Service</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Share Your Information</h2>
          
          <h3>With Other Users</h3>
          <p>Your profile information and photos are visible to other users based on your privacy settings.</p>

          <h3>With Service Providers</h3>
          <p>We share information with third-party service providers who help us operate the Service:</p>
          <ul>
            <li><strong>Payment Processors:</strong> PayPal for payment processing</li>
            <li><strong>Cloud Storage:</strong> Cloudinary for photo storage</li>
            <li><strong>Email Service:</strong> For sending notifications and verification emails</li>
            <li><strong>Hosting:</strong> Render.com for application hosting</li>
          </ul>

          <h3>For Legal Reasons</h3>
          <p>We may disclose your information if required by law or to:</p>
          <ul>
            <li>Comply with legal process or government requests</li>
            <li>Enforce our Terms of Service</li>
            <li>Protect our rights, property, or safety</li>
            <li>Prevent fraud or security issues</li>
          </ul>

          <h3>Business Transfers</h3>
          <p>
            If we are involved in a merger, acquisition, or sale of assets, your information may be 
            transferred. We will notify you before your information becomes subject to a different 
            Privacy Policy.
          </p>
        </section>

        <section>
          <h2>5. Data Retention</h2>
          <p>We retain your information for as long as your account is active or as needed to provide services. When you delete your account:</p>
          <ul>
            <li>Your profile and photos are permanently deleted</li>
            <li>Your messages are removed from conversations</li>
            <li>Some information may be retained for legal or security purposes</li>
          </ul>
          <p>
            <strong>Deactivated accounts:</strong> If you deactivate your account, your data is hidden but 
            retained for 30 days. You can reactivate within this period. After 30 days, your account is 
            permanently deleted.
          </p>
        </section>

        <section>
          <h2>6. Your Privacy Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct your information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and data</li>
            <li><strong>Portability:</strong> Receive your data in a portable format</li>
            <li><strong>Objection:</strong> Object to certain processing of your data</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:privacy@kwikluv.com">privacy@kwikluv.com</a></p>
        </section>

        <section>
          <h2>7. Data Security</h2>
          <p>We implement security measures to protect your information:</p>
          <ul>
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Secure password hashing (bcrypt)</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication</li>
            <li>Secure cloud storage with Cloudinary</li>
          </ul>
          <p>
            However, no method of transmission over the Internet is 100% secure. We cannot guarantee 
            absolute security of your information.
          </p>
        </section>

        <section>
          <h2>8. Cookies and Tracking</h2>
          <p>We use cookies and similar technologies to:</p>
          <ul>
            <li>Keep you logged in</li>
            <li>Remember your preferences</li>
            <li>Analyze usage patterns</li>
            <li>Improve the Service</li>
          </ul>
          <p>You can control cookies through your browser settings, but this may affect functionality.</p>
        </section>

        <section>
          <h2>9. Third-Party Links</h2>
          <p>
            The Service may contain links to third-party websites. We are not responsible for the privacy 
            practices of these sites. We encourage you to read their privacy policies.
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            The Service is not intended for users under 18. We do not knowingly collect information from 
            children. If we discover we have collected information from a child, we will delete it immediately.
          </p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own. We ensure 
            appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes via 
            email or through the Service. Your continued use after changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>If you have questions about this Privacy Policy or your data, contact us:</p>
          <ul>
            <li>Email: <a href="mailto:privacy@kwikluv.com">privacy@kwikluv.com</a></li>
            <li>Support: <Link to="/contact">Contact Form</Link></li>
            <li>Address: KwikLuv, P.O. Box 12345, Johannesburg, South Africa</li>
          </ul>
        </section>

        <section>
          <h2>14. POPIA Compliance (South Africa)</h2>
          <p>
            We comply with the Protection of Personal Information Act (POPIA). You have rights under POPIA 
            including the right to access, correct, and delete your personal information. Contact our 
            Information Officer at <a href="mailto:privacy@kwikluv.com">privacy@kwikluv.com</a> for POPIA-related inquiries.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
